/**
 * @fileoverview Admin routes (login and protected routes for band administrators).
 * @package PlayItNow
 * @module apps/front/routes/admin
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { lazy } from 'react'
import { Route } from 'react-router-dom'
import { ProtectedRoute } from '@/components/ProtectedRoute'

// Lazy load pages for code splitting
const AdminDashboardPage = lazy(() =>
  import('../pages/admin/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })),
)
const SubscriptionPage = lazy(() =>
  import('../pages/admin/SubscriptionPage').then((m) => ({ default: m.SubscriptionPage })),
)
const SubscriptionSuccessPage = lazy(() =>
  import('../pages/admin/SubscriptionSuccessPage').then((m) => ({
    default: m.SubscriptionSuccessPage,
  })),
)
const SettingsPage = lazy(() =>
  import('../pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const BandSettingsPage = lazy(() =>
  import('../pages/admin/BandSettingsPage').then((m) => ({ default: m.BandSettingsPage })),
)
const AdminMembersPage = lazy(() =>
  import('../pages/admin/AdminMembersPage').then((m) => ({ default: m.AdminMembersPage })),
)
const AdminVenuesPage = lazy(() =>
  import('../pages/admin/AdminVenuesPage').then((m) => ({ default: m.AdminVenuesPage })),
)
const AdminEventsPage = lazy(() =>
  import('../pages/admin/AdminEventsPage').then((m) => ({ default: m.AdminEventsPage })),
)
const AdminSongsPage = lazy(() =>
  import('../pages/admin/AdminSongsPage').then((m) => ({ default: m.AdminSongsPage })),
)
const AdminLiveDashboardPage = lazy(() =>
  import('../pages/admin/AdminLiveDashboardPage').then((m) => ({
    default: m.AdminLiveDashboardPage,
  })),
)

/**
 * @summary Admin routes.
 * Includes admin login routes and protected admin routes.
 */
export const adminRoutes = [
  /* Admin protected routes */
  <Route
    key="admin-dashboard"
    path="/admin/dashboard"
    element={
      <ProtectedRoute requireAdmin>
        <AdminDashboardPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="admin-members"
    path="/admin/members"
    element={
      <ProtectedRoute requireAdmin>
        <AdminMembersPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="admin-band"
    path="/admin/band"
    element={
      <ProtectedRoute requireAdmin>
        <BandSettingsPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="subscription"
    path="/subscription"
    element={
      <ProtectedRoute requireAdmin>
        <SubscriptionPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="subscription-success"
    path="/subscription/success"
    element={
      <ProtectedRoute requireAdmin>
        <SubscriptionSuccessPage />
      </ProtectedRoute>
    }
  />,
  /* Alias admin subscription route for convenience */
  <Route
    key="admin-subscription"
    path="/admin/subscription"
    element={
      <ProtectedRoute requireAdmin>
        <SubscriptionPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="admin-user-settings"
    path="/admin/user-settings"
    element={
      <ProtectedRoute requireAdmin>
        <SettingsPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="admin-venues"
    path="/admin/venues"
    element={
      <ProtectedRoute requireAdmin>
        <AdminVenuesPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="admin-events"
    path="/admin/events"
    element={
      <ProtectedRoute requireAdmin>
        <AdminEventsPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="admin-events-id"
    path="/admin/events/:eventId"
    element={
      <ProtectedRoute requireAdmin>
        <AdminEventsPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="admin-events-live"
    path="/admin/events/:eventId/live"
    element={
      <ProtectedRoute requireAdmin>
        <AdminLiveDashboardPage />
      </ProtectedRoute>
    }
  />,
  <Route
    key="admin-songs"
    path="/admin/songs"
    element={
      <ProtectedRoute requireAdmin>
        <AdminSongsPage />
      </ProtectedRoute>
    }
  />,
]
